import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const PriorityContainer = styled(motion.div)`
  position: fixed;
  top: 20px;
  left: 20px;
  background: ${props => props.isPremium ? 
    'linear-gradient(45deg, #4ecdc4, #45b7d1)' : 
    'linear-gradient(45deg, #95a5a6, #7f8c8d)'};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 999;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    top: 10px;
    left: 10px;
    padding: 6px 12px;
    font-size: 0.75rem;
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    if (props.isPremium) {
      return props.status === 'excellent' ? '#2ecc71' : 
             props.status === 'good' ? '#f39c12' : '#e74c3c';
    } else {
      return props.status === 'slow' ? '#e74c3c' : 
             props.status === 'normal' ? '#f39c12' : '#95a5a6';
    }
  }};
  animation: ${props => props.isPremium && props.status === 'excellent' ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const ServerPriorityIndicator = () => {
  const { user, isPremium } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState('normal');
  const [loadingTime, setLoadingTime] = useState(0);

  useEffect(() => {
    if (!user) return;

    // Simulate different connection qualities
    const updateStatus = () => {
      if (isPremium) {
        // Premium users get better connection simulation
        const statuses = ['excellent', 'excellent', 'excellent', 'good'];
        const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
        setConnectionStatus(randomStatus);
        setLoadingTime(Math.random() * 200 + 100); // 100-300ms
      } else {
        // Free users get slower connection simulation
        const statuses = ['slow', 'slow', 'normal', 'normal'];
        const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
        setConnectionStatus(randomStatus);
        setLoadingTime(Math.random() * 800 + 400); // 400-1200ms
      }
    };

    updateStatus();
    const interval = setInterval(updateStatus, 5000 + Math.random() * 5000); // 5-10 seconds

    return () => clearInterval(interval);
  }, [user, isPremium]);

  if (!user) return null;

  const getStatusText = () => {
    if (isPremium) {
      return connectionStatus === 'excellent' ? '⚡ Premium Servers' :
             connectionStatus === 'good' ? '🚀 Fast Connection' : '📶 Connected';
    } else {
      return connectionStatus === 'slow' ? '🐌 Slow Connection' :
             connectionStatus === 'normal' ? '📶 Standard' : '📡 Connected';
    }
  };

  const getLoadingText = () => {
    if (isPremium) {
      return `${Math.round(loadingTime)}ms`;
    } else {
      return `${Math.round(loadingTime)}ms`;
    }
  };

  return (
    <PriorityContainer
      isPremium={isPremium}
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <StatusDot isPremium={isPremium} status={connectionStatus} />
      <span>{getStatusText()}</span>
      <span style={{ opacity: 0.8, fontSize: '0.7em' }}>
        {getLoadingText()}
      </span>
      {!isPremium && (
        <span style={{ 
          fontSize: '0.7em', 
          opacity: 0.9,
          marginLeft: '4px',
          cursor: 'pointer'
        }} onClick={() => window.open('/payment', '_blank')}>
          ⬆️ Upgrade
        </span>
      )}
    </PriorityContainer>
  );
};

export default ServerPriorityIndicator; 